import { useState } from "react";
import { Calculator, ChevronDown, ChevronUp, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useWeight, parseWeightBasedDose, hasWeightBasedDosing, parseFixedDoseVolume } from "@/contexts/WeightContext";

// Fixed dose volume display (for non-weight-based doses like "Fentanyl 25mcg IV")
export const FixedDoseDisplay = ({ text }) => {
  const { concentrations, userConcentrationChoices, setConcentrationChoice } = useWeight();
  
  const results = parseFixedDoseVolume(text, concentrations, userConcentrationChoices);
  if (!results || results.length === 0) return null;

  return (
    <span className="inline-flex items-center gap-1 ml-2 flex-wrap">
      {results.map((calc, idx) => (
        <ConcentrationBadge
          key={idx}
          calc={calc}
          concentrations={concentrations}
          userChoices={userConcentrationChoices}
          onChoiceChange={setConcentrationChoice}
        />
      ))}
    </span>
  );
};

// Inline calculated dose display (shown next to dosing text)
export const InlineDoseDisplay = ({ text }) => {
  const { weight, hasWeight, concentrations, userConcentrationChoices, setConcentrationChoice } = useWeight();
  
  if (!hasWeight || !hasWeightBasedDosing(text)) return null;
  
  const calculations = parseWeightBasedDose(text, weight, concentrations, userConcentrationChoices);
  if (!calculations || calculations.length === 0) return null;
  
  return (
    <span className="inline-flex items-center gap-1 ml-2 flex-wrap">
      {calculations.map((calc, idx) => (
        <span key={idx} className="inline-flex items-center gap-1">
          <Badge 
            variant="default" 
            className={`${calc.capped ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'} text-white font-mono text-xs`}
            data-testid={`inline-dose-${idx}`}
            title={calc.capped ? `Capped at max ${calc.maxDose}` : undefined}
          >
            <Scale className="w-3 h-3 mr-1" />
            {calc.value 
              ? `${calc.value} ${calc.unit}`
              : `${calc.min}-${calc.max} ${calc.unit}`
            }
            {calc.capped && <span className="ml-1 text-[10px] opacity-80">(max)</span>}
          </Badge>
          {(calc.volume || calc.volMin) && (
            <ConcentrationBadge calc={calc} concentrations={concentrations} userChoices={userConcentrationChoices} onChoiceChange={setConcentrationChoice} />
          )}
        </span>
      ))}
    </span>
  );
};

// Blue volume badge with dropdown to switch concentration
const ConcentrationBadge = ({ calc, concentrations, userChoices, onChoiceChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const drugConcs = calc.drug ? concentrations[calc.drug] : null;
  const hasMultiple = drugConcs && drugConcs.length > 1;

  return (
    <span className="relative inline-flex">
      <Badge
        variant="default"
        className={`bg-blue-600 hover:bg-blue-700 text-white font-mono text-xs ${hasMultiple ? 'cursor-pointer pr-1' : ''}`}
        data-testid={`inline-vol-${calc.drug || ''}`}
        title={calc.concLabel ? `Based on ${calc.concLabel}. ${hasMultiple ? 'Click to change.' : ''}` : undefined}
        onClick={hasMultiple ? () => setShowPicker(!showPicker) : undefined}
      >
        {calc.volume
          ? `${calc.volume} mL`
          : `${calc.volMin}-${calc.volMax} mL`
        }
        {hasMultiple && <ChevronDown className="w-3 h-3 ml-0.5 inline" />}
      </Badge>
      {showPicker && hasMultiple && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-popover border border-border rounded-md shadow-lg p-1 min-w-[140px]">
          {drugConcs.map((opt, i) => {
            const isActive = (userChoices?.[calc.drug] === i) || (!userChoices?.[calc.drug] && opt.default);
            return (
              <button
                key={i}
                onClick={() => { onChoiceChange(calc.drug, i); setShowPicker(false); }}
                className={`w-full text-left text-xs px-2 py-1.5 rounded ${isActive ? 'bg-blue-600 text-white' : 'hover:bg-muted text-foreground'}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
};

// Mini calculator widget for protocols
export const ProtocolCalculator = ({ interventions }) => {
  const { weight, setWeight, hasWeight, concentrations, userConcentrationChoices } = useWeight();
  const [expanded, setExpanded] = useState(false);
  const [unit, setUnit] = useState("kg");
  
  const handleWeightChange = (e) => {
    const val = e.target.value;
    if (unit === "lbs") {
      setWeight(val ? (parseFloat(val) / 2.205).toFixed(1) : "");
    } else {
      setWeight(val);
    }
  };

  const displayWeight = unit === "lbs" && hasWeight
    ? (parseFloat(weight) * 2.205).toFixed(1)
    : weight;

  const toggleUnit = () => {
    setUnit(prev => prev === "kg" ? "lbs" : "kg");
  };
  
  // Extract all weight-based doses from interventions
  const weightBasedDoses = [];
  interventions?.forEach(intervention => {
    const text = intervention.text || '';
    if (hasWeightBasedDosing(text)) {
      weightBasedDoses.push({
        text: text,
        calculations: hasWeight ? parseWeightBasedDose(text, weight, concentrations, userConcentrationChoices) : null
      });
    }
    // Check sub-steps too
    intervention.sub_steps?.forEach(sub => {
      if (typeof sub === 'string' && hasWeightBasedDosing(sub)) {
        weightBasedDoses.push({
          text: sub,
          calculations: hasWeight ? parseWeightBasedDose(sub, weight, concentrations, userConcentrationChoices) : null
        });
      }
    });
  });
  
  if (weightBasedDoses.length === 0) return null;
  
  return (
    <Card className="border-green-500/30 bg-green-500/5" data-testid="protocol-calculator">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Calculator className="w-4 h-4 text-green-500" />
            Weight-Based Dosing Calculator
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setExpanded(!expanded)}
            className="h-8"
            data-testid="calculator-toggle"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-4 h-4 text-muted-foreground shrink-0" />
          <Input
            type="number"
            placeholder={`Weight (${unit})`}
            value={displayWeight}
            onChange={handleWeightChange}
            className="w-36 h-9 font-mono bg-background"
            data-testid="protocol-weight-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={toggleUnit}
            className="h-9 px-2 font-mono text-xs shrink-0"
            data-testid="weight-unit-toggle"
          >
            {unit === "kg" ? "kg" : "lbs"}
          </Button>
          {hasWeight && (
            <Badge variant="outline" className="font-mono shrink-0" data-testid="weight-kg-display">
              {weight} kg {unit === "lbs" && `(${displayWeight} lbs)`}
            </Badge>
          )}
        </div>
        
        {expanded && weightBasedDoses.length > 0 && (
          <div className="space-y-2 mt-3 pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">
              {hasWeight ? "Calculated doses for this protocol:" : "Enter weight to see calculated doses"}
            </p>
            {weightBasedDoses.map((dose, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm flex-wrap">
                <span className="text-muted-foreground flex-1 line-clamp-2">{dose.text}</span>
                <div className="flex items-center gap-1 flex-wrap shrink-0">
                  {dose.calculations && dose.calculations.map((calc, cIdx) => (
                    <span key={cIdx} className="inline-flex items-center gap-1">
                      <Badge 
                        className={`${calc.capped ? 'bg-amber-600' : 'bg-green-600'} text-white font-mono shrink-0`}
                        title={calc.capped ? `Capped at max ${calc.maxDose}` : undefined}
                      >
                        {calc.value 
                          ? `${calc.value} ${calc.unit}`
                          : `${calc.min}-${calc.max} ${calc.unit}`
                        }
                        {calc.capped && <span className="ml-1 text-[10px] opacity-80">(max)</span>}
                      </Badge>
                      {(calc.volume || calc.volMin) && (
                        <Badge className="bg-blue-600 text-white font-mono shrink-0" title={calc.concLabel ? `Based on ${calc.concLabel}` : undefined}>
                          {calc.volume ? `${calc.volume} mL` : `${calc.volMin}-${calc.volMax} mL`}
                        </Badge>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Check if a care level has any weight-based dosing
export const careLevelHasWeightDosing = (careLevel) => {
  if (!careLevel?.interventions) return false;
  
  for (const intervention of careLevel.interventions) {
    if (hasWeightBasedDosing(intervention.text)) return true;
    for (const sub of intervention.sub_steps || []) {
      if (typeof sub === 'string' && hasWeightBasedDosing(sub)) return true;
    }
  }
  return false;
};

// Check if protocol has any weight-based dosing
export const protocolHasWeightDosing = (protocol) => {
  if (!protocol?.care_levels) return false;
  return protocol.care_levels.some(level => careLevelHasWeightDosing(level));
};

export default ProtocolCalculator;
